import React, { useEffect, useId, useRef, useState } from 'react';
import cn from 'classnames';
import { CalendarDotsIcon, CaretDownIcon } from '@phosphor-icons/react';
import {
  brDateToISO,
  isoToBrDate,
  maskBrDate,
} from 'volto-ministerio-publico-federal/utils/brDate';
import CalendarPopover from './CalendarPopover';

export interface MPFDatePickerProps {
  label: string;
  /** Data em ISO (`YYYY-MM-DD`); `''` quando não há data. */
  value: string;
  onChange: (value: string) => void;
  /** Limites do calendário, também em ISO. */
  min?: string;
  max?: string;
  id?: string;
  name?: string;
  errorText?: string;
  helperText?: string;
  placeholder?: string;
}

// O campo é montado com as classes do `MPFInput` em vez do componente: o
// `trailingIcon` dele vai dentro de um `aria-hidden`, e aqui o ícone é o botão
// que abre o calendário.
export const MPFDatePicker: React.FC<MPFDatePickerProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  id,
  name,
  errorText,
  helperText,
  placeholder = 'dd/mm/aaaa',
}) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const labelId = `${inputId}-label`;
  const descriptionId = `${inputId}-description`;
  const isInvalid = Boolean(errorText);
  const description = errorText || helperText;

  const [text, setText] = useState(() => isoToBrDate(value));
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Enquanto a data está incompleta o ISO é `''`: comparar pelo ISO derivado
  // evita que o próprio `onChange('')` do meio da digitação apague o texto.
  useEffect(() => {
    setText((current) =>
      brDateToISO(current) === value ? current : isoToBrDate(value),
    );
  }, [value]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);

    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) {
      inputRef.current?.focus();
    }
  };

  const onType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const masked = maskBrDate(event.target.value);

    setText(masked);
    onChange(brDateToISO(masked));
  };

  return (
    <div className="mpf-form-control mpf-datepicker" ref={rootRef}>
      <label htmlFor={inputId} className="mpf-label" id={labelId}>
        <span className="mpf-label-text">{label}</span>
      </label>

      <div
        className={cn('mpf-input', 'mpf-datepicker__field', {
          'mpf-input--error': isInvalid,
        })}
      >
        <CalendarDotsIcon
          className="mpf-datepicker__icon"
          size={18}
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={placeholder}
          className="grow border-0 bg-transparent outline-none"
          value={text}
          onChange={onType}
          aria-invalid={isInvalid || undefined}
          aria-describedby={description ? descriptionId : undefined}
        />
        <button
          type="button"
          className="mpf-datepicker__toggle"
          aria-label={`Escolher ${label.toLowerCase()} no calendário`}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen((current) => !current)}
        >
          <CaretDownIcon size={18} aria-hidden="true" />
        </button>
      </div>

      {description && (
        <span
          id={descriptionId}
          className={cn(
            'mpf-label-text-alt',
            isInvalid ? 'text-error' : 'text-on-surface-low',
          )}
        >
          {description}
        </span>
      )}

      {open && (
        <CalendarPopover
          value={value}
          min={min}
          max={max}
          labelledBy={labelId}
          onSelect={(iso) => {
            setText(isoToBrDate(iso));
            onChange(iso);
            close();
          }}
          onClose={close}
        />
      )}
    </div>
  );
};

export default MPFDatePicker;
